import {Controller, useFormContext} from "react-hook-form";
import {AccountDetailsFormValues} from "../types";
import {FormField, FormFieldError} from "../../../../../../../design-system/form-field";
import {Select, Space, Spin} from "antd";
import {isNonEu} from "../../../../../../../utils/bank";
import {hasBankBranch} from "../utils";
import {
  useGetBankBranches,
  useGetPayoutBanksQuery,
} from "../../../../../../../react-query-hooks/payout";
export function AccountDetailsFormBankFormField({
  isVerifyingAccountNumber,
  isMutationIdle,
  resetMutation,
}: {
  isVerifyingAccountNumber: boolean;
  isMutationIdle: boolean;
  resetMutation: () => void;
}) {
  const {control, watch, setValue} = useFormContext<AccountDetailsFormValues>();
  const countryCodeValue = watch("countryCode");
  const isValidCountryCode =
    isNonEu.includes(countryCodeValue) || hasBankBranch.includes(countryCodeValue);
  const countryCodeQueryValue = isValidCountryCode ? countryCodeValue : null;
  const getPayoutBanksQuery = useGetPayoutBanksQuery({
    countryCode: countryCodeQueryValue,
  });
  const getBankBranches = useGetBankBranches();
  return isValidCountryCode ? (
    <div>
      <FormField
        id="accountBank"
        label="Bank"
        renderInput={(id) => (
          <Controller
            control={control}
            name="bankCode"
            render={({field: {onChange, value, ref, onBlur}, fieldState: {error}}) => (
              <Space direction="vertical" size={16} style={{display: "flex"}}>
                <Select
                  id={id}
                  size="large"
                  status={error?.message ? "error" : undefined}
                  style={{width: "100%"}}
                  onChange={(e) => {
                    if (!isMutationIdle) {
                      resetMutation();
                    }
                    setValue("accountNumber", "");
                    setValue("branchCode", "");
                    onChange(e);

                    const selectedBank = getPayoutBanksQuery.data?.data.find(
                      (b) => b.code === e
                    );
                    if (selectedBank?.hasBankBranch) {
                      getBankBranches.mutate({bankCode: Number(selectedBank.id)});
                    }
                  }}
                  value={value}
                  ref={ref}
                  onBlur={onBlur}
                  showSearch
                  options={
                    getPayoutBanksQuery.isLoading
                      ? []
                      : getPayoutBanksQuery.isError
                      ? []
                      : // Dedupe by code: the API returns some banks with
                        // duplicate codes (e.g. 50572), which collide as Select
                        // option keys and break the virtualized dropdown. Keep
                        // the first occurrence per code.
                        Array.from(
                          getPayoutBanksQuery.data.data
                            .reduce((acc, bank) => {
                              if (!acc.has(bank.code)) {
                                acc.set(bank.code, bank);
                              }
                              return acc;
                            }, new Map<string, (typeof getPayoutBanksQuery.data.data)[number]>())
                            .values()
                        ).map((bank) => ({
                          label: bank.name,
                          value: bank.code,
                        }))
                  }
                  placeholder="Please select"
                  loading={
                    getPayoutBanksQuery.isFetching || getPayoutBanksQuery.isLoading
                  }
                  disabled={
                    getPayoutBanksQuery.isLoading ||
                    getPayoutBanksQuery.isFetching ||
                    getPayoutBanksQuery.isError ||
                    !countryCodeValue ||
                    isVerifyingAccountNumber
                  }
                  optionFilterProp="children"
                  filterOption={(input, option) => {
                    if (option) {
                      return option.label.toLowerCase().includes(input.toLowerCase());
                    } else {
                      return false;
                    }
                  }}
                  notFoundContent={
                    getPayoutBanksQuery.isLoading || getPayoutBanksQuery.isFetching ? (
                      <Spin />
                    ) : null
                  }
                />
                <FormFieldError errorMessage={error?.message} />
              </Space>
            )}
          />
        )}
      />
    </div>
  ) : null;
}
